import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check, X, Images,
  Eye, Maximize2, Sun, Bath, Castle, Star, Mountain, Sparkles, BedDouble, Users,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SUITES as STATIC_SUITES, SUITES_HERO, SHARED_AMENITIES, type Suite } from "./suites/suites-data";
import { listSuitesPublic, getSuitesPageContent, type SuiteWithPhotos } from "@/lib/suites.functions";
import StayEnhancementsSection from "@/components/StayEnhancementsSection";
import { useGalleryGestureLock } from "@/hooks/use-gallery-gesture-lock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import amenitiesPhoto from "@/assets/nature-suite/robes.jpg.asset.json";
import type { CategoryRow, SectionRow } from "@/lib/categories.functions";



type Props = {
  category: CategoryRow;
  sections: SectionRow[];
  appendCta?: boolean;
  experienceSlug?: string | null;
  isActive?: boolean;
};

const TAB_LABELS: Record<string, string> = {
  nature: "Nature Suite",
  "nature-hottub": "Nature Suite with Hot Tub",
  "starview-hottub": "Nature Suite with Starview + Hot Tub",
  "starview-bunk": "Nature Suite with Starview + Bunk",
};

const ICON_MAP: Record<string, LucideIcon> = {
  Eye, Maximize2, Sun, Bath, Castle, Star, Mountain, Sparkles, BedDouble, Users,
};

function dbToSuite(s: SuiteWithPhotos): Suite {
  const photos = s.photos.map((p) => p.url);
  return {
    id: s.slug,
    name: s.name,
    tagline: s.tagline,
    shortDescription: s.short_description,
    longDescription: s.long_description,
    occupancy: s.occupancy,
    bed: s.bed,
    hero: photos[0] ?? "",
    chips: (s.chips ?? []).map((c) => ({ icon: ICON_MAP[c.icon] ?? Star, label: c.label })),
    photos,
  };
}

function SuitesCategoryPageImpl({ experienceSlug, isActive = true }: Props) {
  const fetchSuites = useServerFn(listSuitesPublic);
  const fetchContent = useServerFn(getSuitesPageContent);
  const { data: dbSuites } = useQuery({
    queryKey: ["suites-public", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchSuites({ data: { experience_slug: experienceSlug } })
        : fetchSuites(),
  });
  const { data: pageContent } = useQuery({
    queryKey: ["suites-content", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchContent({ data: { experience_slug: experienceSlug } })
        : fetchContent(),
  });
  const SUITES: Suite[] = dbSuites && dbSuites.length
    ? dbSuites.map(dbToSuite).filter((s) => s.photos.length > 0)
    : STATIC_SUITES;
  const [activeId, setActiveId] = useState<string>(SUITES[0]?.id ?? "");
  useEffect(() => {
    if (SUITES.length && !SUITES.find((s) => s.id === activeId)) setActiveId(SUITES[0].id);
  }, [SUITES, activeId]);
  const active = SUITES.find((s) => s.id === activeId) ?? SUITES[0];
  const [galleryOpen, setGalleryOpen] = useState(false);

  if (!active) return null;

  const heroImage = pageContent?.hero_image_url || SUITES_HERO;
  const heroKicker = pageContent?.hero_kicker || "One-of-a-kind Lodging Experience";
  const heroTitle = pageContent?.hero_title || "Nature Suites";
  const heroSubtitle =
    pageContent?.hero_subtitle ||
    "An experience you can't find anywhere else. No light pollution. No highway noise. Just unobstructed mountain and meadow views and a front row seat to millions of stars";
  const amenitiesKicker = pageContent?.amenities_kicker || "Every nature suite includes";
  const amenitiesImage = pageContent?.amenities_image_url || amenitiesPhoto.url;
  const amenities =
    pageContent && pageContent.amenities.length > 0 ? pageContent.amenities : SHARED_AMENITIES;


  return (
    <div className="relative w-full bg-background">
      {/* Shared hero — static image, fixed aspect so layout is stable */}
      <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
        <img
          src={heroImage}
          alt={heroTitle}
          className="absolute inset-0 w-full h-full object-cover ken-burns"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          draggable={false}
        />
        <div className="mobile-hero-scrim" />
        <div className="mobile-hero-copy px-7">
          <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
            {heroKicker}
          </div>
          {isActive ? (
            <h1 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch] whitespace-pre-line">
              {heroTitle}
            </h1>
          ) : (
            <div className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch] whitespace-pre-line">
              {heroTitle}
            </div>
          )}
          <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
            {heroSubtitle}
          </p>
        </div>

      </section>

      <div className="text-center py-4">
        <span className="text-[10px] tracking-[0.35em] uppercase text-foreground/50">
          OUR 4 SUITE OPTIONS
        </span>
      </div>

      {/* Suite selector + panel — sticky selector is bounded to this wrapper,
          so it releases before the amenities section. */}
      <div className="relative">
        <div
          className="sticky z-20 bg-background/85 backdrop-blur-md border-b border-foreground/10"
          style={{ top: "calc(env(safe-area-inset-top) + 4rem)" }}
        >
          <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
            <div role="tablist" aria-label="Suite type" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SUITES.map((s) => {
                const isActive = s.id === activeId;
                return (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveId(s.id)}
                    className={`group relative flex items-center justify-center min-h-[3.75rem] sm:min-h-[3.25rem] px-2 sm:px-3 py-2 rounded-xl border backdrop-blur-md transition-colors active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold/50 ${
                      isActive
                        ? "border-gold/60 bg-white/10 shadow-[0_0_24px_-6px_rgba(212,175,90,0.45)]"
                        : "border-foreground/10 bg-white/[0.04] hover:bg-white/10 hover:border-gold/40"
                    }`}
                  >
                    <span
                      className={`font-serif text-center leading-[1.15] text-[13px] sm:text-sm md:text-[15px] ${
                        isActive ? "text-gold" : "text-foreground group-hover:text-gold"
                      }`}
                    >
                      {TAB_LABELS[s.id] ?? s.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Active suite panel — no key= so we reuse DOM nodes instead of remounting */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <SuitePanel suite={active} onOpenGallery={() => setGalleryOpen(true)} />
        </section>
      </div>

      {/* Shared amenities */}
      <section className="border-t border-foreground/10 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-stretch">
            {/* Lifestyle photo */}
            <div className="relative w-full aspect-square md:aspect-auto md:h-full overflow-hidden rounded-2xl bg-muted">
              <img
                src={amenitiesImage}
                alt="Inside a Nature Suite"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Amenities list */}
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
                {amenitiesKicker}
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {amenities.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm text-foreground/85">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>

            </div>
          </div>
        </div>
      </section>

      {/* Stay Enhancements */}
      <StayEnhancementsSection
        subtitle={`Tap any card for details and photos.\nAdd at booking or call to add to existing reservations.`}
      />

      {galleryOpen && (
        <GalleryLightbox suite={active} onClose={() => setGalleryOpen(false)} />
      )}
    </div>
  );
}


function SuitePanel({ suite, onOpenGallery }: { suite: Suite; onOpenGallery: () => void }) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setExpanded(false); }, [suite.id]);
  return (
    <div className="w-full">
      {/* Hero — wrapper div with image + overlay gallery trigger.
          Avoid nesting interactive elements (gallery pill inside a button). */}
      <div className="relative w-full aspect-[4/5] sm:aspect-[16/10] overflow-hidden rounded-2xl bg-muted">
        <img
          src={suite.hero}
          alt={suite.name}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        {/* Single button overlay opens the gallery */}
        <button
          type="button"
          onClick={onOpenGallery}
          aria-label={`Open ${suite.name} gallery`}
          className="absolute inset-0 w-full h-full focus:outline-none focus:ring-2 focus:ring-gold/60"
        />
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/95 shadow-md pointer-events-none">
          <Images className="w-3.5 h-3.5" strokeWidth={1.5} />
          {suite.photos.length}
        </span>
      </div>

      {/* Chips */}
      <div className="mt-5 flex flex-wrap gap-2">
        {suite.chips.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/5 px-3 py-1.5 text-[11px] tracking-wider uppercase text-gold/90"
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {label}
          </span>
        ))}
      </div>

      {/* Description */}
      <div className="mt-5">
        <p className={`text-foreground/85 leading-relaxed ${expanded ? "" : "line-clamp-4"}`}>
          {suite.shortDescription}
        </p>
        {!expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-2 text-sm text-gold hover:text-gold/80 transition-colors"
          >
            More...
          </button>
        )}
        {suite.longDescription && (
          <p className="text-foreground/75 leading-relaxed mt-3">{suite.longDescription}</p>
        )}
      </div>

      {/* Occupancy */}
      <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-foreground/10 px-3 py-2.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-foreground/50">Occupancy</div>
          <div className="mt-1 text-foreground/90">{suite.occupancy}</div>
        </div>
        <div className="rounded-lg border border-foreground/10 px-3 py-2.5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-foreground/50">Bed</div>
          <div className="mt-1 text-foreground/90">{suite.bed}</div>
        </div>
      </div>
    </div>
  );
}

function GalleryLightbox({ suite, onClose }: { suite: Suite; onClose: () => void }) {
  const [mounted, setMounted] = useState(false);
  useGalleryGestureLock(mounted);

  useEffect(() => {
    setMounted(true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="gallery-zoom-lock fixed inset-0 z-[100] bg-black"
      role="dialog"
      aria-modal="true"
      aria-label={`${suite.name} gallery`}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        className="fixed right-3 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/70 border border-white/25 text-foreground hover:bg-black/90 transition shadow-lg"
        style={{ top: "calc(env(safe-area-inset-top) + 0.75rem)" }}
      >
        <X className="w-6 h-6" strokeWidth={1.75} />
      </button>

      <div className="gallery-zoom-lock absolute inset-0 overflow-y-auto overscroll-contain">
        <div className="px-4 pt-[calc(env(safe-area-inset-top)+1.25rem)] pb-4 max-w-5xl mx-auto">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-2">Gallery</div>
          <h3 className="font-serif text-2xl sm:text-3xl text-foreground">{suite.name}</h3>
        </div>

        <div className="px-1 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <div className="columns-1 sm:columns-2 gap-1">
            {suite.photos.map((src, i) => (
              <div key={i} className="mb-1 break-inside-avoid">
                <img
                  src={src}
                  alt={`${suite.name} ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="block w-full h-auto"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const SuitesCategoryPage = memo(SuitesCategoryPageImpl);
export default SuitesCategoryPage;
